<template>
  <div>
    <h2 class="mt-2 mt-md-2 mt-md-0">
      How are we doing?
    </h2>
    <span v-if="challenge.challenge_type === 'distance'" class="row">
      <span class="athlete-name p-1 col-4">Distance</span>
      <h3 class="p-1 text-right text-muted col-8 mb-0 mt-0"><strong>{{ formatNumberWithCommas(parseFloat(totalMi).toFixed(2)) }}</strong> of <strong>{{ formatNumberWithCommas(challenge.target) }}</strong> miles</h3>
    </span>
    <div v-if="challenge.challenge_type === 'distance'" class="bar col-12 pl-0 w-100">
      <div class="bar-width d-block" :style="{ width: progress + '%' }"></div>
      <span :class="['bar-target', onTarget ? 'on-target' : 'off-target']">
        {{ onTarget ? 'On target' : 'Keep trying' }}
      </span>
      <span class="bar-digits col-2 text-right">
        {{ parseFloat(progress).toFixed(2) }}%
      </span>
    </div>

    <span v-if="challenge.challenge_type === 'elevation'" class="row mt-3">
      <span class="athlete-name p-1 col-4">Elevation</span>
      <h3 class="p-1 text-right text-muted col-8 mb-0 mt-0"><strong>{{ formatNumberWithCommas(parseFloat(totalEl).toFixed(0)) }}</strong> of <strong>{{ formatNumberWithCommas(challenge.target) }}</strong> feet</h3>
    </span>
    <div v-if="challenge.challenge_type === 'elevation'" class="bar col-12 pl-0 w-100">
      <div class="bar-width d-block" :style="{ width: progress + '%' }"></div>
      <span class="bar-digits col-2 text-right">
        {{ parseFloat(progress).toFixed(2) }}%
      </span>
    </div>

    <span v-if="challenge.challenge_type === 'time'" class="row mt-3">
      <span class="athlete-name p-1 col-4">Activity time</span>
      <h3 class="p-1 text-right text-muted col-8 mb-0 mt-0"><strong>{{ formatNumberWithCommas(parseFloat(totalTi).toFixed(0)) }}</strong> of <strong>{{ formatNumberWithCommas(challenge.target) }}</strong> minutes</h3>
    </span>
    <div v-if="challenge.challenge_type === 'time'" class="bar col-12 pl-0 w-100">
      <div class="bar-width d-block" :style="{ width: progress + '%' }"></div>
      <span class="bar-digits col-2 text-right">
        {{ parseFloat(progress).toFixed(2) }}%
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { numberWithCommas } from '../composables/useFormatters.js';
import { applyOverrideDistances } from '../composables/useAthleteData.js';

const MI = 1609.344;
const FT = 3.28084;

const props = defineProps({
  activities: {
    type: Array,
    required: true,
  },
  challenge: {
    type: Object,
    required: true,
  },
  week: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const progress = ref(0);
const onTarget = ref(false);
const totalMi = ref(0);
const totalEl = ref(0);
const totalTi = ref(0);

onMounted(() => {
  // Apply override distances
  applyOverrideDistances([...props.activities]);
  applyOverrideDistances([...props.week]);

  // Calculate totals
  let totalM = 0;
  let totalE = 0;
  let totalT = 0;

  for (const activity of props.activities) {
    totalM += parseFloat(activity.distance);
    totalT += parseFloat(activity.moving_time);
    if (activity.elevation_gain !== '') {
      totalE += parseFloat(activity.elevation_gain);
    }
  }

  totalMi.value = totalM / MI;
  totalEl.value = totalE * FT;
  totalTi.value = totalT / 60;

  // Calculate progress based on challenge type
  switch (props.challenge.challenge_type) {
  case 'distance':
    progress.value = (totalMi.value / props.challenge.target) * 100;
    break;
  case 'elevation':
    progress.value = (totalEl.value / props.challenge.target) * 100;
    break;
  case 'time':
    progress.value = (totalTi.value / props.challenge.target) * 100;
    break;
  }

  // Calculate if on target
  const today = new Date();
  const endOfYear = new Date(today.getFullYear(), 11, 31);
  const oneDay = 1000 * 60 * 60 * 24;
  const daysLeft = Math.ceil((endOfYear.getTime() - today.getTime()) / oneDay);
  const percDaysLeft = (daysLeft / 365) * 100;

  if (percDaysLeft > (100 - progress.value)) {
    onTarget.value = true;
  }
});

function formatNumberWithCommas(n) {
  return numberWithCommas(n);
}
</script>

<style lang="scss" scoped>
h2.active {
  font-weight: 900;
  color: #174776
}

.bar {
  position: relative;
  background: white;
  border-radius: 25px;
  overflow: hidden;
  .bar-name {
    position: absolute;
    left: 50%;
    top: 12px;
    text-transform: capitalize;
    color: #243546;
    font-weight: 700;
  }
  .bar-width {
    background-color: #174776;
    height: 40px;
    border-radius: 25px;
  }
}

.bar-target {
  position: absolute;
  left: 15px;
  top: 7px;
  text-transform: capitalize;
  color: #174776;
  display: inline-block;
  background: rgba(255, 255, 255, 0.8);
  padding: 6px;
  border-radius: 16px;
  font-size: 14px;

  &.on-target {
    color: #02a517
  }
}

.bar-digits {
  position: absolute;
  right: 15px;
  top: 7px;
  text-transform: capitalize;
  color: #174776;
  display: inline-block;
  background: rgba(255, 255, 255, 0.8);
  padding: 6px;
  border-radius: 16px;
  font-size: 14px;
}

.stats-btn {
  cursor: pointer;
}
</style>
